import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Input,
    OnDestroy,
    OnInit,
    Output,
    ViewChild,
    ViewEncapsulation
} from '@angular/core';
import {map, takeUntil} from "rxjs/operators";
import {Subject} from "rxjs";
import {ListResponse} from "../../services/api/response-types";
import {User} from "../../_models/users";
import {Option} from "../../_typing/query-builder";
import {ApiService} from "../../services/api/api.service";
import {Group} from "../../_models/group";
import {Account} from "../../_models/account";
import {ColumnFormats} from "../../services/table-utils.service";
import {FormControl, ReactiveFormsModule} from "@angular/forms";
import {MatOptgroup, MatSelect} from "@angular/material/select";
import {OptionListSearchComponent} from "../option-list-search/option-list-search.component";
import {allStringFilter} from "../../lib/utils";
import {COMMON_FORM_IMPORTS} from "../../shared/import-groups/Common";
import {FitContentDirective} from "../../directives/fit-content.directive";
import {SharedPipesModule} from "../../shared/modules/shared-pipes.module";
import {CommonModule} from "@angular/common";
import {findOrphanedOption, OrphanedOption} from "../../lib/orphaned-option";

let orphanedDescriptionCounter = 0;

@Component({
    selector: 'select-filter',
    templateUrl: './select-filter.component.html',
    styleUrls: ['./select-filter.component.less'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        OptionListSearchComponent,
        COMMON_FORM_IMPORTS,
        CommonModule,
        ReactiveFormsModule,
        FitContentDirective,
        MatOptgroup,
        SharedPipesModule
    ],
})
export class SelectFilterComponent implements OnInit, OnDestroy {
    @ViewChild('filterSelect') filterSelect: MatSelect;
    @ViewChild(OptionListSearchComponent) searchComponent!: OptionListSearchComponent;

    filterValue: string = "";
    value: FormControl;
    filteredOptions: any;
    errorMessage: string;
    orphanedOption: OrphanedOption | null = null;
    readonly orphanedDescriptionId = `select-filter-orphaned-description-${orphanedDescriptionCounter++}`;
    private readonly onDestroy$ = new Subject<void>();
    private isOpen = false;
    private _modelValue: any;

    get modelValue(): any {
        return this._modelValue;
    }

    @Input() set modelValue(value: any) {
        this._modelValue = value;
        this.value = new FormControl({value: this.modelValue, disabled: this.disabled});
        this.updateOrphanedOption();
        this.changeDetectorRef.markForCheck();
    }

    @Input() noBorder = false;
    @Input() displayTooltip: boolean;
    @Input() filter_compare?: any;
    @Input() app_fit_content: any;
    @Input() conditional_formats: any;
    @Input() formats: ColumnFormats;
    @Input() classes: string;
    @Input() listType: string;
    @Input() label: string;
    private _disabled = false;

    @Input()
    get disabled(): boolean {
        return this._disabled;
    }

    set disabled(value: boolean) {
        this._disabled = value;
        if (!this.value) {
            return;
        }
        this.value[value ? 'disable' : 'enable']();
        this.changeDetectorRef.markForCheck();
    }

    private _options: any[] = [];
    @Input() is_user_options: boolean = false;
    @Input() is_user_group_options: boolean = false;
    @Input() is_account_options: boolean = false;

    @Input() set options(options: any[]) {
        this._options = options;
        this.updateOrphanedOption();
        this.filterOptionList(this.filterValue);
    }

    get options(): any[] {
        return this._options;
    }

    private _groupedOptions: { label: string; children: any[] }[] = [];
    filteredGroupedOptions: { label: string; children: any[] }[] = [];

    @Input() set groupedOptions(groups: { label: string; children: any[] }[]) {
        this._groupedOptions = groups || [];
        this.updateOrphanedOption();
        this.filterOptionList(this.filterValue);
    }

    get groupedOptions(): { label: string; children: any[] }[] {
        return this._groupedOptions;
    }

    @Input() allow_fit: boolean;
    @Input() tab_index: number;
    @Input() placeholder: string;
    @Output() constant_changed: EventEmitter<any> = new EventEmitter();

    constructor(private changeDetectorRef: ChangeDetectorRef, private api: ApiService) {
    }

    ngOnInit(): void {
        this.filteredOptions = this.options;
        this.filteredGroupedOptions = this._groupedOptions;
        this.app_fit_content = this.app_fit_content ?? this.modelValue;

        if (!this.allow_fit) {
            this.allow_fit = this.formats?.allow_fit ?? false;
        }
        if (!this.label) {
            this.classes += ' no-label';
        }

        if (this.is_user_options) {
            this.fetchUserOptions();
        }
        if (this.is_user_group_options) {
            this.fetchUserGroupOptions();
        }
        if (this.is_account_options) {
            this.fetchAccountOptions();
        }
        this.updateOrphanedOption();
    }

    ngOnDestroy(): void {
        this.onDestroy$.next();
        this.onDestroy$.unsubscribe();
    }

    getPlaceholder(): string {
        return this.disabled ? '' : (this.placeholder || 'Click to select');
    }

    onOpen(value: boolean): void {
        this.isOpen = value;
        if (value) {
            if (this.filterSelect?.panel) {
                this.filterSelect.panel.nativeElement.scrollTop = 0;
            }
            this.filterValue = "";
            this.filteredOptions = this.options; // Update filteredOptions after opening
            this.filteredGroupedOptions = this._groupedOptions;
        }
    }

    filterOptionList(filterString: string): void {
        this.filterValue = filterString?.toLowerCase();
        this.filteredOptions = allStringFilter(filterString, this.options);
        this.filteredGroupedOptions = this._groupedOptions
            .map(group => ({
                label: group.label,
                children: allStringFilter(filterString, group.children)
            }))
            .filter(g => g.children?.length > 0);
        const totalCount = (this.filteredOptions?.length || 0) + this.filteredGroupedOptions.reduce((sum, g) => sum + g.children.length, 0);
        if (totalCount === 0) {
            this.errorMessage = `${this.filterValue} does not exist`;
        }
        this.changeDetectorRef.markForCheck();
    }

    onClear(): void {
        this.filterValue = "";
        this.filteredOptions = this.options;
        this.filteredGroupedOptions = this._groupedOptions;
    }

    emitConstantChangedComplete(event: any): void {
        this.modelValue = event;
        this.constant_changed.emit(event);
        this.changeDetectorRef.markForCheck();
    }

    @Input()
    stringFunction: (obj: any, other?: any) => string = (obj) => {
        return obj?.attributes?.name || obj;
    }

    compareByName(option: any, value: any): boolean {
        if (!value) {
            return false;
        }
        if (typeof option === 'object' && option !== null) {
            return option.value === value;
        }
        return option === value;
    }

    /**
     * A stored value that no option can display is rendered as a marked option, so the cell
     * shows what is stored instead of nothing. Recomputed from the unfiltered lists: matching
     * against the filtered ones would report an orphan as soon as the user typed a search term.
     */
    private updateOrphanedOption(): void {
        this.orphanedOption = findOrphanedOption(
            this._modelValue,
            this._options,
            this._groupedOptions,
            this.filter_compare || this.compareByName,
            this.stringFunction,
        );
    }

    private fetchUserOptions(): void {
        this.api.users.searchMany().pipe(
            map((response: ListResponse<User>) =>
                response.data.map((user: User): Option => ({
                    name: user.attributes.name && user.attributes.last_name ? `${user.attributes.name} ${user.attributes.last_name}` : user.attributes.name || user.attributes.alias || user.attributes.email,
                    value: String(user.id)
                }))
            ),
            takeUntil(this.onDestroy$)
        ).subscribe((fetchedOptions: Option[]) => {
            this._options = fetchedOptions;
            this.filteredOptions = this._options;
            this.updateOrphanedOption();
            this.changeDetectorRef.markForCheck();
        });
    }

    private fetchUserGroupOptions(): void {
        this.api.group.searchMany().pipe(
            map((response: ListResponse<Group>) => {
                if (!response || !response.data) {
                    return [];
                }
                return response.data.map((group: Group): Option => ({
                    name: group.attributes.name,
                    value: String(group.id)
                }));
            }),
            takeUntil(this.onDestroy$)
        ).subscribe({
            next: (fetchedOptions: Option[]) => {
                this._options = fetchedOptions;
                this.filteredOptions = this._options;
                this.updateOrphanedOption();
                this.changeDetectorRef.markForCheck();
            },
            error: (error) => {
                this._options = [];
                this.filteredOptions = [];
                this.updateOrphanedOption();
                this.changeDetectorRef.markForCheck();
            }
        });
    }

    private fetchAccountOptions(): void {
        this.api.account.searchMany().pipe(
            map((response: ListResponse<Account>) => {
                if (!response || !response.data) {
                    return [];
                }
                return response.data.map((account: Account): Option => ({
                    name: account.attributes.name,
                    value: account.id
                }));
            }),
            takeUntil(this.onDestroy$)
        ).subscribe({
            next: (fetchedOptions: Option[]) => {
                this._options = fetchedOptions;
                this.filteredOptions = this._options;
                this.updateOrphanedOption();
                this.changeDetectorRef.markForCheck();
            },
            error: (error) => {
                this._options = [];
                this.filteredOptions = [];
                this.updateOrphanedOption();
                this.changeDetectorRef.markForCheck();
            }
        });
    }

}
